// Remplace la cale localStorage : même contrat window.storage.get/set/delete(clé, valeur),
// mais les données sont lues et enregistrées sur le serveur (étape c du Temps 1).
//
// Chaque clé a un numéro de version. Le navigateur retient la version qu'il a lue et la
// renvoie quand il enregistre : si un collègue a enregistré entre-temps, le serveur refuse.
// L'ERP est alors prévenu par l'événement « erp:stockage-conflit » (message + rechargement).
// Serveur injoignable : événement « erp:stockage-erreur ».
// Session expirée ou compte désactivé (réponse 401) : événement « erp:session-expiree ».
// Étape e : modification refusée par les droits (réponse 403) : événement « erp:stockage-refus » ;
// clé interdite à ce compte (lue comme vide) : ses écritures ne partent pas (l'ERP y écrit parfois
// ses valeurs de départ ; le serveur les refuserait de toute façon).
// entetes : en-têtes ajoutés à chaque requête (tests ; dans le navigateur, le cookie de session suffit).
// Listes alimentées par tous les postes (journal d'accès, alertes de Nils) : seuls les
// changements (éléments ajoutés, modifiés, supprimés, repérés par leur id) partent au
// serveur, qui les applique sur la liste à jour. Pas de conflit entre deux postes qui
// ajoutent chacun une ligne. Même liste dans server/src/donnees.js.
const LISTES_PAR_ELEMENTS = new Set(["erp:journal-acces", "erp:alertes"]);

function listeAvecIds(texte) {
  try {
    const l = JSON.parse(texte);
    return Array.isArray(l) && l.every((e) => e && typeof e.id === "string" && e.id) ? l : null;
  } catch (e) {
    return null;
  }
}

// Changements entre la liste connue (avant) et la nouvelle liste de l'ERP (apres).
// suppressionsPermises = false quand « avant » est la liste du serveur et non celle que l'ERP
// a lue : une ligne absente côté ERP vient alors d'un collègue et ne doit pas être effacée.
export function changementsListe(avant, apres, suppressionsPermises = true) {
  const parId = new Map(avant.map((e) => [e.id, JSON.stringify(e)]));
  const idsApres = new Set(apres.map((e) => e.id));
  return {
    ajouts: apres.filter((e) => !parId.has(e.id)),
    modifies: apres.filter((e) => parId.has(e.id) && parId.get(e.id) !== JSON.stringify(e)),
    supprimes: suppressionsPermises ? avant.filter((e) => !idsApres.has(e.id)).map((e) => e.id) : [],
  };
}

// Nils 28/10 : fusion automatique en cas de croisement. « base » = liste que l'ERP croyait avoir,
// « mienne » = celle qu'il veut enregistrer, « serveur » = liste à jour lue après le refus (409).
// Les changements de ce poste (ajouts, modifications, suppressions, par id) sont réappliqués sur la
// liste à jour, sauf si un collègue a changé une des MÊMES fiches entre-temps : renvoie null (vrai
// conflit, message et rechargement comme avant).
export function fusionnerListes(base, mienne, serveur) {
  const mes = changementsListe(base, mienne);
  const parIdBase = new Map(base.map((e) => [e.id, JSON.stringify(e)]));
  const parIdServeur = new Map(serveur.map((e) => [e.id, e]));
  const changeeAilleurs = (id) => {
    const s = parIdServeur.get(id);
    return (s ? JSON.stringify(s) : undefined) !== parIdBase.get(id);
  };
  if ([...mes.modifies.map((e) => e.id), ...mes.supprimes].some(changeeAilleurs)) return null;
  if (mes.ajouts.some((e) => parIdServeur.has(e.id))) return null;
  const modifies = new Map(mes.modifies.map((e) => [e.id, e]));
  const supprimes = new Set(mes.supprimes);
  return [...serveur.filter((e) => !supprimes.has(e.id)).map((e) => modifies.get(e.id) || e), ...mes.ajouts];
}

export function installerStockageServeur(cible, { base = "", entetes = {} } = {}) {
  // Toutes les requêtes partent avec le cookie de session (même site) et les en-têtes donnés.
  const fetch = (url, opts = {}) => globalThis.fetch(url, { credentials: "same-origin", ...opts, headers: { ...entetes, ...(opts.headers || {}) } });
  const versions = new Map();
  // Pour les listes par éléments : dernière liste connue de ce poste.
  const listesConnues = new Map();
  // Les écritures d'une même clé partent l'une après l'autre (sinon la seconde
  // partirait avec une version déjà dépassée par la première).
  const files = new Map();
  const interdites = new Set();
  // Après un conflit, plus aucune écriture ne part de ce poste avant le rechargement :
  // l'état affiché n'est plus à jour.
  let bloque = false;
  // Nils 20/10 : actualisation sans tout recharger. Dernière valeur connue de chaque clé (lue ou
  // enregistrée par ce poste) ; pendant une actualisation, seules les clés de « aRecharger » sont
  // redemandées au serveur, les autres sont rendues depuis cette mémoire.
  const brutes = new Map();
  // Version de chaque clé telle que ce poste la connaît vraiment (lue, ou enregistrée en entier).
  const connues = new Map();
  let aRecharger = null;
  // Le journal d'accès grossit à chaque action de chacun : il ne déclenche pas d'actualisation
  // (il est relu à l'ouverture de l'ERP ; le serveur l'écrit lui-même).
  const SANS_ACTUALISATION = new Set(["erp:journal-acces"]);
  // Fusion automatique (Nils 28/10) : liste que l'ERP croit avoir pour chaque clé (lue ou enregistrée),
  // et clés dont l'écran n'a pas encore les changements d'un collègue fusionnés : tant que l'ERP ne
  // les a pas relues, chaque écriture repasse par la fusion (sinon la liste entière envoyée
  // effacerait ces changements).
  const bases = new Map();
  const divergentes = new Set();
  const TENTATIVES_FUSION = 3;

  const url = (cle) => `${base}/api/donnees/${encodeURIComponent(cle)}`;
  const signaler = (type, detail) => cible.dispatchEvent(new CustomEvent(type, { detail }));

  function enFile(cle, tache) {
    const suite = (files.get(cle) || Promise.resolve()).then(tache, tache);
    files.set(cle, suite);
    return suite;
  }

  // Version de départ d'une écriture. Si la clé n'a pas encore été lue pendant cette session
  // (ex. l'ERP n'a pas fini de charger), l'écriture n'est acceptée que si la clé est vide sur
  // le serveur : sinon elle écraserait des données sans les avoir vues. Renvoie null dans ce cas.
  async function versionDeDepart(cle) {
    if (versions.has(cle)) return versions.get(cle);
    const rep = await fetch(url(cle));
    if (!rep.ok) throw new Error(`Lecture de ${cle} impossible (HTTP ${rep.status})`);
    const { valeur, version } = await rep.json();
    if (valeur !== null) return null;
    return version;
  }

  function conflit(cle) {
    bloque = true;
    signaler("erp:stockage-conflit", { cle });
    return false;
  }

  // surCroisement : appelé sur un refus pour version dépassée (409) au lieu du conflit.
  async function envoyer(cle, requete, surCroisement) {
    if (bloque) return conflit(cle);
    let rep;
    try {
      const version = await versionDeDepart(cle);
      if (version === null) return conflit(cle);
      rep = await requete(version);
    } catch (e) {
      signaler("erp:stockage-erreur", { cle, message: String(e) });
      return false;
    }
    const corps = await rep.json().catch(() => ({}));
    if (rep.ok) {
      versions.set(cle, corps.version);
      return true;
    }
    if (rep.status === 401) {
      signaler("erp:session-expiree", { cle });
      return false;
    }
    if (rep.status === 403) {
      bloque = true;
      signaler("erp:stockage-refus", { cle, message: corps.message || "Modification refusée." });
      return false;
    }
    if (rep.status === 409) return surCroisement ? surCroisement() : conflit(cle);
    signaler("erp:stockage-erreur", { cle, message: `HTTP ${rep.status}` });
    return false;
  }

  // Lit la liste à jour (vue de ce compte), y réapplique les changements de ce poste et l'enregistre.
  async function fusionner(cle, mienne, journal) {
    const base = bases.get(cle);
    if (!base) return conflit(cle);
    for (let i = 0; i < TENTATIVES_FUSION; i++) {
      let lu;
      try {
        const rep = await fetch(url(cle));
        if (rep.status === 401) {
          signaler("erp:session-expiree", { cle });
          return false;
        }
        if (!rep.ok) throw new Error(`Lecture de ${cle} impossible (HTTP ${rep.status})`);
        lu = await rep.json();
      } catch (e) {
        signaler("erp:stockage-erreur", { cle, message: String(e) });
        return false;
      }
      const serveur = lu.valeur === null ? [] : listeAvecIds(lu.valeur);
      const fusion = serveur && fusionnerListes(base, mienne, serveur);
      if (!fusion) return conflit(cle);
      let encore = false;
      versions.set(cle, lu.version);
      const ok = await envoyer(cle, (version) => fetch(url(cle), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ valeur: JSON.stringify(fusion), version, ...(journal ? { journal } : {}) }),
      }), () => { encore = true; return false; });
      if (ok) {
        divergentes.add(cle);
        bases.set(cle, mienne);
        brutes.set(cle, JSON.stringify(mienne));
        // L'écran n'a pas les changements du collègue : la clé sera relue à la prochaine actualisation.
        connues.set(cle, -1);
        signaler("erp:stockage-fusion", { cle });
        return true;
      }
      if (!encore) return false;
    }
    return conflit(cle);
  }

  async function envoyerChangements(cle, texte) {
    if (bloque) return conflit(cle);
    const apres = listeAvecIds(texte);
    try {
      let avant = listesConnues.get(cle);
      let suppressionsPermises = true;
      if (!avant) {
        // Liste pas encore lue par ce poste : on part de celle du serveur, sans rien y effacer.
        const rep = await fetch(url(cle));
        if (!rep.ok) throw new Error(`Lecture de ${cle} impossible (HTTP ${rep.status})`);
        const { valeur } = await rep.json();
        avant = (valeur !== null && listeAvecIds(valeur)) || [];
        suppressionsPermises = false;
      }
      const rep = await fetch(`${base}/api/listes/${encodeURIComponent(cle)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changementsListe(avant, apres, suppressionsPermises)),
      });
      if (rep.status === 401) {
        signaler("erp:session-expiree", { cle });
        return false;
      }
      if (!rep.ok) throw new Error(`HTTP ${rep.status}`);
      const { version } = await rep.json();
      versions.set(cle, version);
      listesConnues.set(cle, apres);
      // La liste du serveur contient aussi les lignes des collègues : elle sera relue à la prochaine actualisation.
      brutes.delete(cle);
      return true;
    } catch (e) {
      signaler("erp:stockage-erreur", { cle, message: String(e) });
      return false;
    }
  }

  cible.storage = {
    // Vérification du démarrage de l'ERP : le serveur et sa base répondent.
    async verifier() {
      try {
        const rep = await fetch(`${base}/api/sante`);
        return rep.ok;
      } catch (e) {
        return false;
      }
    },
    // options.pourComparer : lecture qui ne sert pas de base à une modification (ex. journal
    // d'accès) ; elle ne change pas la version retenue pour la prochaine écriture.
    async get(cle, partage, options) {
      if (aRecharger && !aRecharger.has(cle) && brutes.has(cle) && !(options && options.pourComparer)) {
        const v = brutes.get(cle);
        return v === null ? null : { value: v };
      }
      let rep;
      try {
        rep = await fetch(url(cle));
      } catch (e) {
        signaler("erp:stockage-erreur", { cle, message: String(e) });
        throw e;
      }
      if (!rep.ok) {
        signaler(rep.status === 401 ? "erp:session-expiree" : "erp:stockage-erreur", { cle, message: `HTTP ${rep.status}` });
        throw new Error(`Lecture de ${cle} impossible (HTTP ${rep.status})`);
      }
      const { valeur, version, interdit, lectureSeule } = await rep.json();
      if (!(options && options.pourComparer)) {
        brutes.set(cle, valeur);
        connues.set(cle, version);
        const l = valeur === null ? [] : listeAvecIds(valeur);
        if (l) bases.set(cle, l);
        else bases.delete(cle);
        divergentes.delete(cle);
      }
      // Clé interdite ou en lecture seule pour ce compte : les écritures de l'ERP n'y partent pas
      // (mises à jour automatiques à l'ouverture ; le serveur les refuserait).
      if (interdit || lectureSeule) interdites.add(cle);
      if (!(options && options.pourComparer) || !versions.has(cle)) {
        versions.set(cle, version);
        if (LISTES_PAR_ELEMENTS.has(cle)) {
          const l = valeur === null ? [] : listeAvecIds(valeur);
          if (l) listesConnues.set(cle, l);
        }
      }
      return valeur === null ? null : { value: valeur };
    },
    // Nils 20/10 (a, b) : clés lues par ce poste dont la version a changé sur le serveur depuis
    // (un collègue a enregistré). Renvoie un tableau, ou null si le serveur ne répond pas.
    async clesChangees() {
      try {
        const rep = await fetch(`${base}/api/versions`);
        if (!rep.ok) return null;
        const { versions: serveur } = await rep.json();
        return [...connues.keys()].filter((cle) => !SANS_ACTUALISATION.has(cle) && serveur[cle] !== undefined && serveur[cle] !== connues.get(cle));
      } catch (e) {
        return null;
      }
    },
    // Début et fin d'une actualisation : entre les deux, les lectures des clés non listées sont
    // servies depuis la mémoire de ce poste (aucune requête).
    debutActualisation(cles) {
      aRecharger = new Set(cles);
    },
    finActualisation() {
      aRecharger = null;
    },
    // Étape d : le journal d'accès est écrit par le serveur (cf. ecrireStockage dans app.jsx).
    journalServeur: true,
    // Étape f : prochain numéro d'une série (FA, BL, DEV…), attribué par le serveur.
    // Renvoie { numero }, { depart: true } (étape h : numéro de départ de la série à saisir par le
    // Gérant) ou null si impossible.
    async numero(serie, annee) {
      try {
        const rep = await fetch(`${base}/api/numeros`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(annee === undefined ? { serie } : { serie, annee: Number(annee) }),
        });
        if (rep.status === 401) {
          signaler("erp:session-expiree", { cle: serie });
          return null;
        }
        if (rep.status === 409) return { depart: true };
        if (!rep.ok) return null;
        const n = (await rep.json()).numero;
        return n ? { numero: n } : null;
      } catch (e) {
        signaler("erp:stockage-erreur", { cle: serie, message: String(e) });
        return null;
      }
    },
    // Étape h : numéros de départ (Gérant). departs() : état des séries ; saisirDepart : { ok } ou { erreur }.
    async departs() {
      try {
        const rep = await fetch(`${base}/api/numeros/departs`);
        return rep.ok ? (await rep.json()).series : null;
      } catch (e) {
        return null;
      }
    },
    async saisirDepart(serie, dernier, annee) {
      try {
        const rep = await fetch(`${base}/api/numeros/depart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ serie, dernier, ...(annee === undefined ? {} : { annee: Number(annee) }) }),
        });
        const corps = await rep.json().catch(() => ({}));
        return rep.ok ? { ok: true } : { erreur: corps.message || `Refusé (erreur ${rep.status}).` };
      } catch (e) {
        return { erreur: "Serveur injoignable." };
      }
    },
    // Étape g : dépôt d'un fichier sur un devis (envoyé brut, rangé par le serveur dans le stockage
    // objet). Renvoie { fichier: { id, nom, taille, type } } ou { erreur: message }.
    // Nils 30/10 : cible = { cle, element } pour joindre un fichier à une facture fournisseur.
    async deposerFichier(devisId, fichier, cible) {
      try {
        const q = cible ? `cle=${encodeURIComponent(cible.cle)}&element=${encodeURIComponent(cible.element)}` : `devis=${encodeURIComponent(devisId)}`;
        const rep = await fetch(`${base}/api/fichiers?${q}`, {
          method: "POST",
          headers: { "Content-Type": "application/octet-stream", "X-Nom-Fichier": encodeURIComponent(fichier.name) },
          body: fichier,
        });
        if (rep.status === 401) {
          signaler("erp:session-expiree", { cle: "fichier" });
          return { erreur: "Session expirée." };
        }
        const corps = await rep.json().catch(() => ({}));
        if (!rep.ok) return { erreur: corps.message || `Dépôt refusé (erreur ${rep.status}).` };
        return { fichier: corps.fichier };
      } catch (e) {
        return { erreur: "Serveur injoignable : le fichier n'a pas été envoyé." };
      }
    },
    // Adresse d'ouverture d'un fichier : le serveur revérifie les droits à chaque ouverture.
    lienFichier(id) {
      return `${base}/api/fichiers/${encodeURIComponent(id)}`;
    },
    // Fichiers retirés depuis moins de 30 jours (Gérant) : liste, ou null si impossible.
    async fichiersRetires() {
      try {
        const rep = await fetch(`${base}/api/fichiers-retires`);
        return rep.ok ? (await rep.json()).fichiers : null;
      } catch (e) {
        return null;
      }
    },
    // Restaure un fichier retiré (Gérant). Renvoie { ok: true } ou { erreur: message }.
    async restaurerFichier(id) {
      try {
        const rep = await fetch(`${base}/api/fichiers/${encodeURIComponent(id)}/restaurer`, { method: "POST" });
        const corps = await rep.json().catch(() => ({}));
        return rep.ok ? { ok: true } : { erreur: corps.message || `Restauration refusée (erreur ${rep.status}).` };
      } catch (e) {
        return { erreur: "Serveur injoignable." };
      }
    },
    // options.journal : ligne de journal { type, detail }, ajoutée par le serveur si l'enregistrement réussit.
    set(cle, valeur, partage, options) {
      if (interdites.has(cle) && !LISTES_PAR_ELEMENTS.has(cle)) return Promise.resolve(true);
      if (LISTES_PAR_ELEMENTS.has(cle) && listeAvecIds(String(valeur))) return enFile(cle, () => envoyerChangements(cle, String(valeur)));
      const journal = options && options.journal ? { type: String(options.journal.type), detail: String(options.journal.detail || "") } : undefined;
      // Étape f : import ou « Vider des données » du Gérant (dispensés des contrôles jusqu'à l'étape h).
      const operation = options && ["import", "vidage"].includes(options.operation) ? options.operation : undefined;
      return enFile(cle, async () => {
        const mienne = operation ? null : listeAvecIds(String(valeur));
        const fusionPossible = !!mienne && bases.has(cle);
        // Changements d'un collègue déjà fusionnés mais pas encore relus par l'écran : on refusionne.
        if (fusionPossible && divergentes.has(cle) && !bloque) return fusionner(cle, mienne, journal);
        let croisement = false;
        const ok = await envoyer(cle, (version) =>
          fetch(url(cle), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ valeur: String(valeur), version, ...(journal ? { journal } : {}), ...(operation ? { operation } : {}) }),
          }),
          fusionPossible ? () => { croisement = true; return false; } : undefined,
        );
        if (croisement) return fusionner(cle, mienne, journal);
        if (ok) {
          brutes.set(cle, String(valeur));
          connues.set(cle, versions.get(cle));
          if (mienne) bases.set(cle, mienne);
          else bases.delete(cle);
        }
        return ok;
      });
    },
    delete(cle) {
      if (interdites.has(cle)) return Promise.resolve(true);
      return enFile(cle, async () => {
        const ok = await envoyer(cle, (version) => fetch(`${url(cle)}?version=${version}`, { method: "DELETE" }));
        if (ok) {
          brutes.set(cle, null);
          connues.set(cle, versions.get(cle));
        }
        return ok;
      });
    },
  };
}
